import { streamAdminAdhocProxySpeed, streamAdminProxySpeed } from '../utils/adminProxySpeedStream';
import { createProbeProgressContext, reduceProbeProgressEvent } from '../utils/adminSpeedProbeProgress';

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useBackdropClose } from "../hooks/useBackdropClose";

const DialogCloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SpeedArrowDownIcon = ({ className }) => (
  <svg
    className={className}
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 5v11.5M12 16.5l-4.25-4.25M12 16.5l4.25-4.25"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SpeedArrowUpIcon = ({ className }) => (
  <svg
    className={className}
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 19V7.5M12 7.5L7.75 11.75M12 7.5l4.25 4.25"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Полный оборот стрелки по дуге: 0 … GAUGE_MAX_MBPS (значения выше — у края шкалы). */
const GAUGE_MAX_MBPS = 60;
/** Дуга макета: M 20 100 A 80 80 0 0 1 180 100 → длина полукруга π·r (в макете 251.2). */
const GAUGE_ARC_PATH = "M 20 100 A 80 80 0 0 1 180 100";
const GAUGE_ARC_LENGTH = 251.2;

/** Плавный откат (мс) → пауза 2 с → рывок к пику → 2 с дребезг у пика → короткий сброс. */
const NEEDLE_RETREAT_MS = 380;
const NEEDLE_HOLD_MS = 2000;
const NEEDLE_LUNGE_MS = 220;
/** «Удар» о пик (град.), затем затухает в фазе дребезга. */
const NEEDLE_OVERSHOOT_DEG = 11;
/** Дребезг у пика: длительность и частота (Гц) — прилипание/отлипание от пика. */
const NEEDLE_PEAK_RATTLE_MS = 2000;
const NEEDLE_PEAK_RATTLE_HZ = 4;
const NEEDLE_PEAK_RATTLE_AMP = 5.8;
const NEEDLE_PEAK_RATTLE_OVERSHOOT_DECAY_MS = 240;
/** Плавное затухание дребезга в конце окна (мс). */
const NEEDLE_PEAK_RATTLE_FADE_MS = 280;
const NEEDLE_RETREAT_DEG = 6;
/** Догон стрелки при падении цели (мягче). */
const NEEDLE_SMOOTH_DECEL = 0.072;
/** Мин/макс догон при росте скорости — чем больше отставание, тем ближе к max (ощущение «набора»). */
const NEEDLE_SMOOTH_ACCEL_MIN = 0.14;
const NEEDLE_SMOOTH_ACCEL_MAX = 0.42;
/** Импульс стрелки вперёд при скачке mbps (затухание за кадр). */
const NEEDLE_SURGE_DECAY = 0.84;
const NEEDLE_SURGE_PER_MBPS = 2.35;
const NEEDLE_SURGE_MAX = 16;
/** Сглаживание цифры в центре шкалы к фактическому mbps (активная фаза). */
const DISPLAY_MBPS_SMOOTH = 0.22;

const easeOutCubic = (x) => 1 - (1 - x) ** 3;
const easeInCubic = (x) => x * x * x;

const SpeedGauge = ({ label, mbps, active, id }) => {
  const uid = useId().replace(/:/g, "");
  const gradId = `gaugeGradient_${uid}`;
  const fadeMaskId = `fadeMask_${uid}`;
  const gaugeGlowFilterId = `gaugeGlow_${uid}`;
  const smoothedBaseRef = useRef(0);
  const smoothedMbpsTextRef = useRef(0);
  const surgeDegRef = useRef(0);
  const prevMbpsSampleRef = useRef(0);
  const [gaugeFrame, setGaugeFrame] = useState({ needleDeg: 0, displayMbps: 0 });
  const cycleStartRef = useRef(performance.now());
  const peakMbpsRef = useRef(0);
  const rafRef = useRef(0);
  const mbpsDisplayRef = useRef(mbps);

  mbpsDisplayRef.current = mbps;

  useEffect(() => {
    if (mbps <= 0) {
      peakMbpsRef.current = 0;
    } else if (mbps > peakMbpsRef.current) {
      peakMbpsRef.current = mbps;
      cycleStartRef.current = performance.now();
    }
  }, [mbps]);

  useEffect(() => {
    if (active) {
      cycleStartRef.current = performance.now();
      const m = mbpsDisplayRef.current;
      smoothedMbpsTextRef.current = m;
      prevMbpsSampleRef.current = m;
      surgeDegRef.current = 0;
      setGaugeFrame((prev) => ({
        ...prev,
        displayMbps: Number.isFinite(m) && m >= 0 ? m : prev.displayMbps,
      }));
    }
  }, [active]);

  useEffect(() => {
    const cycleMs =
      NEEDLE_RETREAT_MS + NEEDLE_HOLD_MS + NEEDLE_LUNGE_MS + NEEDLE_PEAK_RATTLE_MS;
    const lungeStart = NEEDLE_RETREAT_MS + NEEDLE_HOLD_MS;
    const rattleStart = lungeStart + NEEDLE_LUNGE_MS;

    const tick = (now) => {
      const rawMbps = mbpsDisplayRef.current;
      const tNorm = Math.min(1, Math.max(0, rawMbps / GAUGE_MAX_MBPS));
      const targetBase = 180 * tNorm;

      if (active) {
        const mbpsJump = Math.max(0, rawMbps - prevMbpsSampleRef.current);
        prevMbpsSampleRef.current = rawMbps;
        if (mbpsJump > 0.04) {
          const jumpBoost = 1 + tNorm * 0.65;
          surgeDegRef.current += Math.min(NEEDLE_SURGE_MAX, mbpsJump * NEEDLE_SURGE_PER_MBPS * jumpBoost);
        }
      } else {
        prevMbpsSampleRef.current = rawMbps;
      }
      surgeDegRef.current *= NEEDLE_SURGE_DECAY;
      if (surgeDegRef.current < 0.02) {
        surgeDegRef.current = 0;
      }
      surgeDegRef.current = Math.min(NEEDLE_SURGE_MAX, surgeDegRef.current);

      const err = targetBase - smoothedBaseRef.current;
      const smooth =
        err > 0.04
          ? Math.min(
              NEEDLE_SMOOTH_ACCEL_MAX,
              NEEDLE_SMOOTH_ACCEL_MIN + Math.min(120, err) * 0.0028,
            )
          : NEEDLE_SMOOTH_DECEL;
      smoothedBaseRef.current += err * smooth;
      if (Math.abs(smoothedBaseRef.current - targetBase) < 0.045) {
        smoothedBaseRef.current = targetBase;
      }

      let wobble = 0;
      if (active) {
        const elapsed = now - cycleStartRef.current;
        const phaseT = elapsed % cycleMs;
        if (phaseT < NEEDLE_RETREAT_MS) {
          const p = phaseT / NEEDLE_RETREAT_MS;
          wobble = easeOutCubic(p) * -NEEDLE_RETREAT_DEG;
        } else if (phaseT < lungeStart) {
          wobble = -NEEDLE_RETREAT_DEG;
        } else if (phaseT < rattleStart) {
          const p = (phaseT - lungeStart) / NEEDLE_LUNGE_MS;
          wobble = -NEEDLE_RETREAT_DEG + easeInCubic(p) * (NEEDLE_OVERSHOOT_DEG + NEEDLE_RETREAT_DEG);
        } else {
          const t = phaseT - rattleStart;
          const overshootDecay = Math.exp(-t / NEEDLE_PEAK_RATTLE_OVERSHOOT_DECAY_MS);
          const sine =
            NEEDLE_PEAK_RATTLE_AMP *
            Math.sin((2 * Math.PI * NEEDLE_PEAK_RATTLE_HZ * t) / 1000);
          let fadeEnvelope = 1;
          if (t > NEEDLE_PEAK_RATTLE_MS - NEEDLE_PEAK_RATTLE_FADE_MS) {
            fadeEnvelope = Math.max(0, (rattleStart + NEEDLE_PEAK_RATTLE_MS - phaseT) / NEEDLE_PEAK_RATTLE_FADE_MS);
          }
          wobble = NEEDLE_OVERSHOOT_DEG * overshootDecay + sine * fadeEnvelope;
        }
      }

      const targetMbps = mbpsDisplayRef.current;
      if (active && targetMbps >= 0 && Number.isFinite(targetMbps)) {
        smoothedMbpsTextRef.current += (targetMbps - smoothedMbpsTextRef.current) * DISPLAY_MBPS_SMOOTH;
        if (Math.abs(smoothedMbpsTextRef.current - targetMbps) < 0.015) {
          smoothedMbpsTextRef.current = targetMbps;
        }
      } else {
        smoothedMbpsTextRef.current =
          targetMbps >= 0 && Number.isFinite(targetMbps) ? targetMbps : smoothedMbpsTextRef.current;
      }

      setGaugeFrame({
        needleDeg: smoothedBaseRef.current + surgeDegRef.current + wobble,
        displayMbps: smoothedMbpsTextRef.current,
      });
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [active]);

  const textMbpsSource =
    mbps >= 0 && Number.isFinite(mbps)
      ? active
        ? gaugeFrame.displayMbps
        : mbps
      : Number.NaN;
  const dialMbpsText = Number.isFinite(textMbpsSource) ? textMbpsSource.toFixed(1) : "—";
  const progressT = Number.isFinite(textMbpsSource)
    ? Math.min(1, Math.max(0, textMbpsSource / GAUGE_MAX_MBPS))
    : 0;
  const progressDashOffset = GAUGE_ARC_LENGTH * (1 - progressT);
  return (
    <div className={`proxy-speed-gauge${active ? " proxy-speed-gauge--active" : ""}`}>
      <div className="proxy-speed-gauge__label" id={id}>
        {label}
      </div>
      <svg
        className="proxy-speed-gauge__svg proxy-speed-gauge__svg--dial"
        viewBox="0 0 200 120"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-labelledby={id}
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#93c5fd" stopOpacity={0.5} />
            <stop offset="5%" stopColor="#93c5fd" stopOpacity={1} />
            <stop offset="13.3%" stopColor="#4ade80" />
            <stop offset="33.3%" stopColor="#22c55e" />
            <stop offset="100%" stopColor="#15803d" />
          </linearGradient>
          <filter id={gaugeGlowFilterId} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <mask id={fadeMaskId}>
            <path
              d={GAUGE_ARC_PATH}
              fill="none"
              stroke="white"
              strokeWidth="20"
              strokeLinecap="round"
            />
            <circle cx="100" cy="100" r="90" fill="black" opacity="0.3">
              <animate attributeName="opacity" values="0;0.4;0" dur="5s" repeatCount="indefinite" />
            </circle>
          </mask>
        </defs>

        <path
          className="proxy-speed-gauge__dial-track"
          d={GAUGE_ARC_PATH}
          fill="none"
          stroke="#f1f5f9"
          strokeWidth="20"
          strokeLinecap="round"
        />

        <path
          d={GAUGE_ARC_PATH}
          fill="none"
          stroke={`url(#${gradId})`}
          strokeWidth="20"
          strokeLinecap="round"
          filter={`url(#${gaugeGlowFilterId})`}
          strokeDasharray={GAUGE_ARC_LENGTH}
          strokeDashoffset={progressDashOffset}
        />

        <g className="proxy-speed-gauge__needle" transform="translate(100 100)">
          <g transform={`rotate(${gaugeFrame.needleDeg.toFixed(2)} 0 0)`}>
            <line
              x1="-70"
              y1="0"
              x2="-10"
              y2="0"
              stroke="#374151"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <circle cx="0" cy="0" r="8" fill="#1f2937" />
          </g>
        </g>

        <text x="20" y="115" className="proxy-speed-gauge__dial-scale-end">
          0
        </text>
        <text x="170" y="115" className="proxy-speed-gauge__dial-scale-end">
          60
        </text>

        <text x="100" y="72" textAnchor="middle" className="proxy-speed-gauge__dial-value">
          {dialMbpsText}
        </text>
        <text x="100" y="85" textAnchor="middle" className="proxy-speed-gauge__dial-unit">
          Мбит/с
        </text>
      </svg>
    </div>
  );
};

const toAdhocProxyType = (proxyType) =>
  String(proxyType || "http").toUpperCase().includes("SOCK") ? "socks5" : "http";

export const AdminProxySpeedTestModal = ({ row, onClose, onComplete, onProbeProgress }) => {
  const titleId = useId();
  const dlGaugeLabelId = useId();
  const ulGaugeLabelId = useId();
  /** Родитель часто передаёт новую inline-функцию onProbeProgress / меняет onComplete — без ref useEffect с потоком срывается на каждом рендере. */
  const onCompleteRef = useRef(onComplete);
  const onProbeProgressRef = useRef(onProbeProgress);
  onCompleteRef.current = onComplete;
  onProbeProgressRef.current = onProbeProgress;

  const [phase, setPhase] = useState("");
  const [externalIp, setExternalIp] = useState(null);
  const [downloadMbps, setDownloadMbps] = useState(0);
  const [uploadMbps, setUploadMbps] = useState(0);
  const [finished, setFinished] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [finalDownload, setFinalDownload] = useState(null);
  const [finalUpload, setFinalUpload] = useState(null);
  const [finalDownloadPeak, setFinalDownloadPeak] = useState(null);
  const [finalUploadPeak, setFinalUploadPeak] = useState(null);
  const [speedTestRunNonce, setSpeedTestRunNonce] = useState(0);
  const completedRef = useRef(false);
  const dlPeakRef = useRef(0);
  const ulPeakRef = useRef(0);
  const probeProgressCtxRef = useRef(createProbeProgressContext());
  const rowId = row
    ? row.external_key != null
      ? String(row.external_key)
      : String(row.proxy_id)
    : "";

  const handleEvent = useCallback(
    (ev) => {
      const ui = reduceProbeProgressEvent(ev, probeProgressCtxRef.current);
      const notify = onProbeProgressRef.current;
      if (ui && typeof notify === "function") {
        notify(ui);
      }
      if (ev.event === "progress") {
        if (ev.phase === "ip" && ev.external_ip) {
          setExternalIp(ev.external_ip);
        }
        if (ev.phase === "download") {
          setPhase("Загрузка");
          const raw = typeof ev.mbps === "number" ? ev.mbps : 0;
          dlPeakRef.current = Math.max(dlPeakRef.current, raw);
          setDownloadMbps(dlPeakRef.current);
        }
        if (ev.phase === "upload") {
          setPhase("Отдача");
          const raw = typeof ev.mbps === "number" ? ev.mbps : 0;
          ulPeakRef.current = Math.max(ulPeakRef.current, raw);
          setUploadMbps(ulPeakRef.current);
        }
      }
      if (ev.event === "phase") {
        if (ev.phase === "download") {
          setPhase("Загрузка");
          dlPeakRef.current = 0;
          setDownloadMbps(0);
        }
        if (ev.phase === "upload") {
          setPhase("Отдача");
          ulPeakRef.current = 0;
          setUploadMbps(0);
        }
      }
      if (ev.event === "start") {
        setPhase("Подключение");
      }
      if (ev.event === "done") {
        setFinished(true);
        if (ev.external_ip) {
          setExternalIp(ev.external_ip);
        }
        const d = typeof ev.download_mbps === "number" ? ev.download_mbps : 0;
        const u = typeof ev.upload_mbps === "number" ? ev.upload_mbps : 0;
        const dlPeak = dlPeakRef.current;
        const ulPeak = ulPeakRef.current;
        setFinalDownload(d);
        setFinalUpload(u);
        setFinalDownloadPeak(dlPeak > 0 ? dlPeak : null);
        setFinalUploadPeak(ulPeak > 0 ? ulPeak : null);
        setDownloadMbps(dlPeak > 0 ? dlPeak : d);
        setUploadMbps(ulPeak > 0 ? ulPeak : u);
        if (ev.error) {
          setErrorText(ev.error);
        } else if (!completedRef.current && rowId) {
          completedRef.current = true;
          const speedCheckedAt = new Date().toLocaleString("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          });
          /** Те же числа, что в блоке «Скорость:» (пик, иначе среднее за этап). */
          const outDl = dlPeak > 0 ? dlPeak : d;
          const outUl = ulPeak > 0 ? ulPeak : u;
          const cb = onCompleteRef.current;
          if (typeof cb === "function") {
            cb({
              rowId,
              downloadMbps: outDl,
              uploadMbps: outUl,
              downloadMbpsAvg: d,
              uploadMbpsAvg: u,
              downloadMbpsMax: dlPeak > 0 ? dlPeak : null,
              uploadMbpsMax: ulPeak > 0 ? ulPeak : null,
              speedCheckedAt,
            });
          }
        }
      }
    },
    [rowId]
  );

  useEffect(() => {
    if (!row) {
      return;
    }
    const ac = new AbortController();
    completedRef.current = false;
    dlPeakRef.current = 0;
    ulPeakRef.current = 0;
    probeProgressCtxRef.current = createProbeProgressContext();
    const prep = onProbeProgressRef.current;
    if (typeof prep === "function") {
      prep({ percent: 0, label: "Подготовка…" });
    }
    setPhase("Старт");
    setExternalIp(null);
    setDownloadMbps(0);
    setUploadMbps(0);
    setFinished(false);
    setErrorText(null);
    setFinalDownload(null);
    setFinalUpload(null);
    setFinalDownloadPeak(null);
    setFinalUploadPeak(null);

    void (async () => {
      try {
        if (row.is_external && row.external_key) {
          const portNum = Number.parseInt(String(row.port), 10);
          await streamAdminAdhocProxySpeed(
            {
              host: String(row.host || "").trim(),
              port: portNum,
              proxy_type: toAdhocProxyType(row.proxy_type),
              username: row.login || row.username || null,
              password: row.password || null,
            },
            { signal: ac.signal, onEvent: handleEvent }
          );
        } else if (row.proxy_id != null) {
          await streamAdminProxySpeed(row.proxy_id, { signal: ac.signal, onEvent: handleEvent });
        } else {
          setErrorText("Нет идентификатора прокси для замера");
          setFinished(true);
        }
      } catch (e) {
        if (ac.signal.aborted) {
          return;
        }
        const msg = e instanceof Error ? e.message : "Ошибка замера";
        setErrorText(msg);
        setFinished(true);
      }
    })();

    return () => {
      ac.abort();
    };
  }, [row, handleEvent, speedTestRunNonce]);

  const handleRetrySpeedTest = useCallback(() => {
    setSpeedTestRunNonce((n) => n + 1);
  }, []);

  // Затемнение закрывает окно только настоящим щелчком — нажатие и отпускание на нём (владелец 29.10, DF85)
  const backdrop = useBackdropClose(onClose);

  if (!row) {
    return null;
  }

  const dlActive = !finished && phase === "Загрузка";
  const ulActive = !finished && phase === "Отдача";

  return createPortal(
    <div className="proxy-speed-test-overlay" role="presentation" {...backdrop}>
      <div
        className="proxy-speed-test-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="proxy-speed-test-dialog__head">
          <h2 className="proxy-speed-test-dialog__title" id={titleId}>
            Скорость прокси
          </h2>
          <button type="button" className="proxy-speed-test-dialog__close" aria-label="Закрыть" onClick={onClose}>
            <DialogCloseIcon />
          </button>
        </div>
        <div className="proxy-speed-test-dialog__body">
          <p className="proxy-speed-test-dialog__host">
            {row.host}:{row.port} · {row.proxy_type || row.proxyType}
          </p>
          <div className="proxy-speed-test-dialog__ip-row">
            <span className="proxy-speed-test-dialog__ip-label">Текущий IP через прокси</span>
            <span className="proxy-speed-test-dialog__ip-value">{externalIp ?? "—"}</span>
          </div>
          <p className="proxy-speed-test-dialog__phase" aria-live="polite">
            {!finished ? phase || "…" : errorText ? "Завершено с ошибкой" : "Готово"}
          </p>
          <div className="proxy-speed-test-dialog__gauges">
            <SpeedGauge label="Входящая" mbps={downloadMbps} active={dlActive} id={dlGaugeLabelId} />
            <SpeedGauge label="Исходящая" mbps={uploadMbps} active={ulActive} id={ulGaugeLabelId} />
          </div>
          {finished && !errorText ? (
            <div className="proxy-speed-test-dialog__summary" role="status">
              <div className="proxy-speed-test-dialog__summary-row">
                <strong>Скорость:</strong>{" "}
                <span className="proxy-speed-test-dialog__summary-metric">
                  <SpeedArrowDownIcon className="proxy-speed-test-dialog__speed-arrow proxy-speed-test-dialog__speed-arrow--down" />
                  {finalDownloadPeak?.toFixed(2) ?? finalDownload?.toFixed(2) ?? "—"} Мбит/с
                </span>
                <span className="proxy-speed-test-dialog__summary-sep" aria-hidden="true">
                  {" "}
                  ·{" "}
                </span>
                <span className="proxy-speed-test-dialog__summary-metric">
                  <SpeedArrowUpIcon className="proxy-speed-test-dialog__speed-arrow proxy-speed-test-dialog__speed-arrow--up" />
                  {finalUploadPeak?.toFixed(2) ?? finalUpload?.toFixed(2) ?? "—"} Мбит/с
                </span>
              </div>
              <div className="proxy-speed-test-dialog__summary-avg proxy-speed-test-dialog__summary-row">
                <strong>Средняя скорость:</strong>{" "}
                <span className="proxy-speed-test-dialog__summary-metric">
                  <SpeedArrowDownIcon className="proxy-speed-test-dialog__speed-arrow proxy-speed-test-dialog__speed-arrow--down" />
                  {finalDownload?.toFixed(2) ?? "—"} Мбит/с
                </span>
                <span className="proxy-speed-test-dialog__summary-sep" aria-hidden="true">
                  {" "}
                  ·{" "}
                </span>
                <span className="proxy-speed-test-dialog__summary-metric">
                  <SpeedArrowUpIcon className="proxy-speed-test-dialog__speed-arrow proxy-speed-test-dialog__speed-arrow--up" />
                  {finalUpload?.toFixed(2) ?? "—"} Мбит/с
                </span>
              </div>
            </div>
          ) : null}
          {errorText ? <div className="proxy-speed-test-dialog__error" role="alert">{errorText}</div> : null}
        </div>
        <div className="proxy-speed-test-dialog__footer">
          {finished ? (
            <button
              type="button"
              className="proxy-speed-test-dialog__btn proxy-speed-test-dialog__btn--secondary"
              onClick={handleRetrySpeedTest}
            >
              Повторить
            </button>
          ) : null}
          <button type="button" className="proxy-speed-test-dialog__btn proxy-speed-test-dialog__btn--primary" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
